import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BrandLockup } from '../Brand/Logo';
import {
  AppBar,
  Toolbar,
  Box,
  Container,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Home as HomeIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  Search as SearchIcon,
  Chat as ChatIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  Groups as GroupsIcon,
  Settings as SettingsIcon,
  Map as MapIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  RateReview as RateReviewIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useColorMode } from '../../contexts/ColorModeContext';
import { useCookieConsent } from '../../contexts/CookieConsentContext';
import { initials } from '../../utils/strings';
import { apiGetThreads, apiListClubPosts, apiListCoursePosts, apiListFriendRequests, apiListFriends, apiListMyClubs, apiListMyCourses } from '../../services/api';
import { connectIfAuth } from '../../services/socket';
import CookieConsentBanner from './CookieConsentBanner';
import CookieSettingsDialog from './CookieSettingsDialog';
import IntroRoadmapDialog, { type IntroRoadmapStep } from './IntroRoadmapDialog';
import NotificationsBell from '../Notifications/NotificationsBell';

interface LayoutProps {
  children: React.ReactNode;
}

type UnseenProbeResult = {
  allIds: string[];
  checkedIds: string[];
  unseenCheckedIds: string[];
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user: me, loading: authLoading, logout } = useAuth();
  const { consent, openSettingsDialog } = useCookieConsent();
  const { mode, toggle: toggleColorMode } = useColorMode();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [unreadTotal, setUnreadTotal] = React.useState(0);
  const [hasCourseUpdates, setHasCourseUpdates] = React.useState(false);
  const [hasClubUpdates, setHasClubUpdates] = React.useState(false);
  const [hasIncomingFriendRequests, setHasIncomingFriendRequests] = React.useState(false);
  const [introRoadmapOpen, setIntroRoadmapOpen] = React.useState(false);
  const [introRoadmapLoading, setIntroRoadmapLoading] = React.useState(false);
  const [introRoadmapProgress, setIntroRoadmapProgress] = React.useState({
    hasJoinedClub: false,
    hasJoinedCourse: false,
    hasFriend: false,
  });
  const [hasSeenIntroRoadmap, setHasSeenIntroRoadmap] = React.useState(true);
  const nextCourseProbeIndexRef = React.useRef(0);
  const nextClubProbeIndexRef = React.useRef(0);

  const courseSeenMapKey = React.useMemo(() => (me?.id ? `dragonsframe_seen_course_channels_${me.id}` : null), [me?.id]);
  const clubSeenMapKey = React.useMemo(() => (me?.id ? `dragonsframe_seen_club_channels_${me.id}` : null), [me?.id]);
  const unseenCourseIdsKey = React.useMemo(() => (me?.id ? `dragonsframe_unseen_course_ids_${me.id}` : null), [me?.id]);
  const unseenClubIdsKey = React.useMemo(() => (me?.id ? `dragonsframe_unseen_club_ids_${me.id}` : null), [me?.id]);
  const introRoadmapSeenKey = React.useMemo(() => (me?.id ? `dragonsframe_intro_roadmap_seen_${me.id}` : null), [me?.id]);
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/verify-email' || location.pathname === '/reset-password';

  const hasInterests = (me?.interests?.length ?? 0) > 0;
  const hasProfileNotification = !hasInterests || hasIncomingFriendRequests;
  const hasSettingsNotification = !consent.resolved;
  const hasIntroRoadmapNotification = !hasSeenIntroRoadmap;
  const hasAccountMenuNotifications = hasProfileNotification || hasSettingsNotification || hasIntroRoadmapNotification;

  const pickProbeIds = React.useCallback((ids: string[], startIndex: number, maxChecks: number) => {
    if (!ids.length || maxChecks <= 0) return { picked: [] as string[], nextIndex: 0 };
    const count = Math.min(maxChecks, ids.length);
    const start = ((startIndex % ids.length) + ids.length) % ids.length;
    const picked: string[] = [];
    for (let i = 0; i < count; i += 1) {
      picked.push(ids[(start + i) % ids.length]);
    }
    return { picked, nextIndex: (start + count) % ids.length };
  }, []);

  const readSeenMap = React.useCallback((key: string | null): Record<string, number> => {
    if (!key) return {};
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const normalized: Record<string, number> = {};
      Object.entries(parsed || {}).forEach(([id, value]) => {
        const num = Number(value);
        if (Number.isFinite(num)) normalized[id] = num;
      });
      return normalized;
    } catch {
      return {};
    }
  }, []);

  const writeUnseenIds = React.useCallback((key: string | null, ids: string[]) => {
    if (!key) return;
    localStorage.setItem(key, JSON.stringify(ids));
  }, []);

  const readUnseenIds = React.useCallback((key: string | null): string[] => {
    if (!key) return [];
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? (JSON.parse(raw) as unknown) : [];
      return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
    } catch {
      return [];
    }
  }, []);

  const mergeProbeWithStoredUnseen = React.useCallback((stored: string[], probe: UnseenProbeResult): string[] => {
    const allSet = new Set(probe.allIds);
    const checkedSet = new Set(probe.checkedIds);
    const next = stored.filter((id) => allSet.has(id) && !checkedSet.has(id));
    for (const id of probe.unseenCheckedIds) {
      if (allSet.has(id) && !next.includes(id)) next.push(id);
    }
    return next;
  }, []);

  const getUnseenCourseProbe = React.useCallback(async (): Promise<UnseenProbeResult> => {
    const MAX_CHECKS_PER_CYCLE = 3;
    const seenMap = readSeenMap(courseSeenMapKey);
    const resp: any = await apiListMyCourses();
    const ids = (resp?.items || []).map((c: any) => c?.id).filter(Boolean) as string[];
    const { picked, nextIndex } = pickProbeIds(ids, nextCourseProbeIndexRef.current, MAX_CHECKS_PER_CYCLE);
    nextCourseProbeIndexRef.current = nextIndex;
    const checked: string[] = [];
    const unseen: string[] = [];

    for (const id of picked) {
      try {
        // Use take=2 to bypass cached take=1 probe responses and avoid stale notifications.
        const posts: any = await apiListCoursePosts(id, undefined, 5);
        const items = posts?.items || [];
        // Ignore the user's OWN posts so your own message doesn't flag the channel as "new".
        const latestOther = items.find((p: any) => (p?.author?.id ?? p?.authorId) !== me?.id);
        const createdAt = latestOther?.createdAt ? Date.parse(latestOther.createdAt) : 0;
        const seenAt = seenMap[id] ?? 0;
        checked.push(id);
        if (createdAt > seenAt) unseen.push(id);
      } catch {
        // Skip per-channel failures and continue checking others.
      }
    }
    return { allIds: ids, checkedIds: checked, unseenCheckedIds: unseen };
  }, [courseSeenMapKey, pickProbeIds, readSeenMap, me?.id]);

  const getUnseenClubProbe = React.useCallback(async (): Promise<UnseenProbeResult> => {
    const MAX_CHECKS_PER_CYCLE = 3;
    const seenMap = readSeenMap(clubSeenMapKey);
    const resp: any = await apiListMyClubs();
    const ids = (resp?.items || []).map((c: any) => c?.id).filter(Boolean) as string[];
    const { picked, nextIndex } = pickProbeIds(ids, nextClubProbeIndexRef.current, MAX_CHECKS_PER_CYCLE);
    nextClubProbeIndexRef.current = nextIndex;
    const checked: string[] = [];
    const unseen: string[] = [];

    for (const id of picked) {
      try {
        // Use take=2 to bypass cached take=1 probe responses and avoid stale notifications.
        const posts: any = await apiListClubPosts(id, undefined, 5);
        const items = posts?.items || [];
        // Ignore the user's OWN posts so your own message doesn't flag the channel as "new".
        const latestOther = items.find((p: any) => (p?.author?.id ?? p?.authorId) !== me?.id);
        const createdAt = latestOther?.createdAt ? Date.parse(latestOther.createdAt) : 0;
        const seenAt = seenMap[id] ?? 0;
        checked.push(id);
        if (createdAt > seenAt) unseen.push(id);
      } catch {
        // Skip per-channel failures and continue checking others.
      }
    }
    return { allIds: ids, checkedIds: checked, unseenCheckedIds: unseen };
  }, [clubSeenMapKey, pickProbeIds, readSeenMap, me?.id]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
    navigate('/login');
  };

  const handleProfileClick = () => {
    handleMenuClose();
    // Always open the profile in view mode. The "add interests" nudge lives in the
    // roadmap dialog's explicit action — visiting your own profile shouldn't force edit.
    navigate('/profile');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleSettingsClick = () => {
    handleMenuClose();
    navigate('/settings');
    if (!consent.resolved) {
      window.setTimeout(() => {
        openSettingsDialog();
      }, 0);
    }
  };

  const refreshRoadmapProgress = React.useCallback(async () => {
    if (!me || isAuthPage) return;
    setIntroRoadmapLoading(true);
    try {
      const [clubs, courses, friends] = await Promise.all([
        apiListMyClubs(),
        apiListMyCourses(),
        apiListFriends(undefined, undefined, 1),
      ]);

      const joinedClubCount = Array.isArray((clubs as any)?.items) ? (clubs as any).items.length : 0;
      const joinedCourseCount = Array.isArray((courses as any)?.items) ? (courses as any).items.length : 0;
      const friendCount = Array.isArray((friends as any)?.items) ? (friends as any).items.length : 0;

      setIntroRoadmapProgress({
        hasJoinedClub: joinedClubCount > 0,
        hasJoinedCourse: joinedCourseCount > 0,
        hasFriend: friendCount > 0,
      });
    } catch {
      // Keep current state if status checks fail.
    } finally {
      setIntroRoadmapLoading(false);
    }
  }, [isAuthPage, me]);

  const handleCloseIntroRoadmap = React.useCallback(() => {
    if (introRoadmapSeenKey) localStorage.setItem(introRoadmapSeenKey, '1');
    setHasSeenIntroRoadmap(true);
    setIntroRoadmapOpen(false);
  }, [introRoadmapSeenKey]);

  const handleOpenIntroRoadmap = () => {
    handleMenuClose();
    setIntroRoadmapOpen(true);
    void refreshRoadmapProgress();
  };

  const roadmapSteps = React.useMemo<IntroRoadmapStep[]>(() => [
    {
      id: 'interests',
      title: 'Tell us your interests',
      description: 'Add interests on your profile so people with similar hobbies can find you.',
      actionLabel: hasInterests ? 'Review' : 'Add Interests',
      completed: hasInterests,
      path: '/profile?edit=1',
    },
    {
      id: 'clubs',
      title: 'Join a club',
      description: 'Explore clubs and join at least one to unlock community discussions.',
      actionLabel: introRoadmapProgress.hasJoinedClub ? 'Open Clubs' : 'Browse Clubs',
      completed: introRoadmapProgress.hasJoinedClub,
      path: '/clubs',
    },
    {
      id: 'courses',
      title: 'Join your classes',
      description: 'Join your course channels to read updates and chat with classmates.',
      actionLabel: introRoadmapProgress.hasJoinedCourse ? 'Open Courses' : 'Find Courses',
      completed: introRoadmapProgress.hasJoinedCourse,
      path: '/courses',
    },
    {
      id: 'friends',
      title: 'Text your friends',
      description: 'Add friends from Search and start direct messages to stay connected.',
      actionLabel: introRoadmapProgress.hasFriend ? 'Open Messages' : 'Find People',
      completed: introRoadmapProgress.hasFriend,
      path: introRoadmapProgress.hasFriend ? '/messages' : '/search',
    },
  ], [hasInterests, introRoadmapProgress.hasFriend, introRoadmapProgress.hasJoinedClub, introRoadmapProgress.hasJoinedCourse]);

  const handleGoToRoadmapStep = React.useCallback((step: IntroRoadmapStep) => {
    handleCloseIntroRoadmap();
    navigate(step.path);
  }, [handleCloseIntroRoadmap, navigate]);

  // Poll unread count; gate on auth and use backoff on errors
  React.useEffect(() => {
    connectIfAuth(me);
    let timeoutId: number | null = null;

    if (!me || isAuthPage) {
      setUnreadTotal(0);
      setHasCourseUpdates(false);
      setHasClubUpdates(false);
      setHasIncomingFriendRequests(false);
      nextCourseProbeIndexRef.current = 0;
      nextClubProbeIndexRef.current = 0;
      if (!me) {
        writeUnseenIds(unseenCourseIdsKey, []);
        writeUnseenIds(unseenClubIdsKey, []);
      }
      return () => { connectIfAuth(null); };
    }
    let alive = true, interval = 120000;
    async function load() {
      if (!alive) return;
      try {
        const [threads, incomingReqs, courseProbe, clubProbe] = await Promise.all([
          apiGetThreads(),
          apiListFriendRequests('incoming', undefined, 1),
          getUnseenCourseProbe(),
          getUnseenClubProbe(),
        ]);
        if (!alive) return;
        const sum = (threads ?? []).reduce((n: number, t: any) => n + (t.unreadCount ?? 0), 0);
        const nextCourseUnseen = mergeProbeWithStoredUnseen(readUnseenIds(unseenCourseIdsKey), courseProbe);
        const nextClubUnseen = mergeProbeWithStoredUnseen(readUnseenIds(unseenClubIdsKey), clubProbe);
        setUnreadTotal(sum);
        setHasIncomingFriendRequests((incomingReqs?.items?.length ?? 0) > 0);
        setHasCourseUpdates(nextCourseUnseen.length > 0);
        setHasClubUpdates(nextClubUnseen.length > 0);
        writeUnseenIds(unseenCourseIdsKey, nextCourseUnseen);
        writeUnseenIds(unseenClubIdsKey, nextClubUnseen);
        interval = 120000; // reset after success
      } catch (e) {
        interval = Math.min(interval * 2, 5 * 60 * 1000); // backoff up to 5m
      } finally {
        if (alive) timeoutId = window.setTimeout(load, interval);
      }
    }
    void load();
    return () => {
      alive = false;
      if (timeoutId !== null) window.clearTimeout(timeoutId);
      connectIfAuth(null);
    };
  }, [me, isAuthPage, getUnseenCourseProbe, getUnseenClubProbe, mergeProbeWithStoredUnseen, readUnseenIds, unseenCourseIdsKey, unseenClubIdsKey, writeUnseenIds]);

  React.useEffect(() => {
    if (!me) return;

    const syncFromStorage = () => {
      setHasCourseUpdates(readUnseenIds(unseenCourseIdsKey).length > 0);
      setHasClubUpdates(readUnseenIds(unseenClubIdsKey).length > 0);
    };

    syncFromStorage();
    const intervalId = window.setInterval(syncFromStorage, 5000);
    window.addEventListener('focus', syncFromStorage);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', syncFromStorage);
    };
  }, [me, readUnseenIds, unseenCourseIdsKey, unseenClubIdsKey]);

  React.useEffect(() => {
    if (!me || isAuthPage) {
      setIntroRoadmapOpen(false);
      setHasSeenIntroRoadmap(true);
      setIntroRoadmapProgress({ hasJoinedClub: false, hasJoinedCourse: false, hasFriend: false });
      return;
    }

    void refreshRoadmapProgress();
    const hasSeenRoadmap = introRoadmapSeenKey ? localStorage.getItem(introRoadmapSeenKey) === '1' : true;
    setHasSeenIntroRoadmap(hasSeenRoadmap);
    if (!hasSeenRoadmap) setIntroRoadmapOpen(true);
  }, [introRoadmapSeenKey, isAuthPage, me, refreshRoadmapProgress]);

  const navValue = React.useMemo(() => {
    const p = location.pathname;
    if (p.startsWith('/messages')) return '/messages';
    if (p.startsWith('/professors')) return '/professors';
    return p;
  }, [location.pathname]);

  const isLegalPage = React.useMemo(() => {
    const p = location.pathname;
    return p === '/terms' || p === '/privacy' || p === '/cookies' || p.startsWith('/legal/');
  }, [location.pathname]);

  // Don't show layout for auth pages
  if (isAuthPage) {
    return (
      <>
        {children}
        <CookieConsentBanner />
        <CookieSettingsDialog />
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <Box
            component="button"
            onClick={() => navigate('/')}
            aria-label="Dragonsframe home"
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              background: 'none',
              border: 0,
              p: 0,
              m: 0,
              cursor: 'pointer',
            }}
          >
            <BrandLockup size={22} textColor={mode === 'dark' ? '#F5ECE0' : '#FFFFFF'} ember={mode === 'dark' ? '#FF6A3D' : '#FFB152'} />
          </Box>
          <IconButton color="inherit" onClick={toggleColorMode} aria-label="Toggle light/dark mode" sx={{ mr: me || authLoading ? 0.5 : 0 }}>
            {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
          {/* Reserve the bell+avatar footprint while /me hydrates so the theme toggle
              doesn't paint at the far right and jump ~96px left a moment later. */}
          {!me && authLoading && <Box aria-hidden sx={{ width: 96, height: 40 }} />}
          {me && <NotificationsBell />}
          {me && (
            <IconButton
              size="large"
              onClick={handleMenuOpen}
              color="inherit"
              aria-label="Account menu"
            >
              <Badge
                color="error"
                variant="dot"
                overlap="circular"
                invisible={!hasAccountMenuNotifications}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Avatar src={(me as any).profileImage || undefined} sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                  {initials(me.displayName || `${me.firstName} ${me.lastName}`)}
                </Avatar>
              </Badge>
            </IconButton>
          )}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleProfileClick}>
              <Badge
                color="error"
                variant="dot"
                overlap="circular"
                invisible={!hasProfileNotification}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ mr: 1 }}
              >
                <AccountIcon />
              </Badge>
              Profile
            </MenuItem>
            <MenuItem onClick={handleSettingsClick}>
              <Badge
                color="error"
                variant="dot"
                overlap="circular"
                invisible={!hasSettingsNotification}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ mr: 1 }}
              >
                <SettingsIcon />
              </Badge>
              Settings
            </MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); navigate('/events'); }}>
              <EventIcon sx={{ mr: 1 }} />
              Campus Events
            </MenuItem>
            <MenuItem onClick={handleOpenIntroRoadmap}>
              <Badge
                color="error"
                variant="dot"
                overlap="circular"
                invisible={!hasIntroRoadmapNotification}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ mr: 1 }}
              >
                <MapIcon />
              </Badge>
              Intro Roadmap
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Container
        component="main"
        sx={{
          flexGrow: 1,
          py: 2,
          pb: isLegalPage ? 2 : 10,
        }}
      >
        {children}
      </Container>

      {!isLegalPage && (
        <Paper
          sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }}
          elevation={3}
        >
          <BottomNavigation
            value={navValue}
            onChange={(_event, newValue) => {
              handleNavigation(newValue);
            }}
            showLabels={!isPhone}
            sx={{
              bgcolor: 'transparent',
              // Active item uses the Home design accent: ember in dark, deep blue in light.
              '& .Mui-selected, & .Mui-selected .MuiBottomNavigationAction-label, & .Mui-selected .MuiSvgIcon-root': {
                color: mode === 'dark' ? '#FF6A3D' : '#26408B',
              },
              ...(isPhone ? {
                '& .MuiBottomNavigationAction-root': { minWidth: 0, px: 0.5 },
                '& .MuiBottomNavigationAction-label': { fontSize: '0.62rem' },
              } : {}),
            }}
          >
            <BottomNavigationAction
              label="Home"
              value="/"
              icon={<HomeIcon />}
            />
            <BottomNavigationAction
              label="Courses"
              value="/courses"
              icon={
                <Badge
                  color="error"
                  variant="dot"
                  overlap="circular"
                  invisible={!hasCourseUpdates}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <SchoolIcon />
                </Badge>
              }
            />
            <BottomNavigationAction
              label="Profs"
              value="/professors"
              icon={<RateReviewIcon />}
            />
            <BottomNavigationAction
              label="Roommates"
              value="/roommates"
              icon={<PeopleIcon />}
            />
            <BottomNavigationAction
              label="Clubs"
              value="/clubs"
              icon={
                <Badge
                  color="error"
                  variant="dot"
                  overlap="circular"
                  invisible={!hasClubUpdates}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <GroupsIcon />
                </Badge>
              }
            />
            <BottomNavigationAction
              label="Search"
              value="/search"
              icon={<SearchIcon />}
            />
            <BottomNavigationAction
              label={isPhone ? 'Msgs' : 'Messages'}
              value="/messages"
              icon={
                <Badge
                  color="error"
                  variant="dot"
                  overlap="circular"
                  invisible={unreadTotal <= 0}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <ChatIcon />
                </Badge>
              }
            />
          </BottomNavigation>
        </Paper>
      )}

      <CookieConsentBanner />
      <CookieSettingsDialog />
      <IntroRoadmapDialog
        open={introRoadmapOpen}
        loading={introRoadmapLoading}
        steps={roadmapSteps}
        onClose={handleCloseIntroRoadmap}
        onGoToStep={handleGoToRoadmapStep}
      />
    </Box>
  );
};

export default Layout; 